import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL - handles both old and new formats
const getImageUrl = (url) => {
  if (!url) return '';
  // Already a full URL
  if (url.startsWith('http')) {
    // Fix old format: if URL contains /uploads/ without /api/, add /api
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  // Relative path - add BACKEND_URL
  // If path doesn't start with /api, add it
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

const AdminDashboard = () => {
  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
    } else {
      setToken(adminToken);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[#1A1915]">
      {/* Admin Header */}
      <header className="bg-[#1A1915] border-b border-[#C9A961]/20 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 bg-[#1A1915] p-2">
                <img
                  src="/images/goldlogo.webp"
                  alt="Legato d'Oro"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="btn-gold text-sm px-6 py-2"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#221F1A] border-r border-[#C9A961]/20 min-h-[calc(100vh-73px)] sticky top-[73px]">
          <nav className="p-6 space-y-2">
            <Link
              to="/admin/dashboard"
              data-testid="nav-dashboard"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/dashboard'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              📊 Dashboard
            </Link>
            <Link
              to="/admin/products"
              data-testid="nav-products"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/products'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              🍾 Products
            </Link>
            <Link
              to="/admin/recipes"
              data-testid="nav-recipes"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/recipes'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              🍳 Recipes
            </Link>
            <Link
              to="/admin/gallery"
              data-testid="nav-gallery"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/gallery'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              🖼️ Gallery
            </Link>
            <Link
              to="/admin/messages"
              data-testid="nav-messages"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/messages'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              💬 Messages
            </Link>
            <Link
              to="/admin/newsletter"
              data-testid="nav-newsletter"
              className={`block px-4 py-3 rounded transition-colors ${
                location.pathname === '/admin/newsletter'
                  ? 'bg-[#C9A961] text-black font-semibold'
                  : 'text-gray-400 hover:text-[#C9A961] hover:bg-[#2A2720]'
              }`}
            >
              📧 Newsletter
            </Link>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/products" element={<ProductsManager token={token} />} />
            <Route path="/recipes" element={<RecipesManager token={token} />} />
            <Route path="/gallery" element={<GalleryManager token={token} />} />
            <Route path="/messages" element={<MessagesManager token={token} />} />
            <Route path="/newsletter" element={<NewsletterManager token={token} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  const [stats, setStats] = useState({ products: 0, recipes: 0, messages: 0, subscribers: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, recipesRes, messagesRes, newsletterRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/recipes`),
        axios.get(`${API}/contact`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
        }),
        axios.get(`${API}/newsletter/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
        })
      ]);
      setStats({
        products: productsRes.data.length,
        recipes: recipesRes.data.length,
        messages: messagesRes.data.filter(m => !m.read).length,
        subscribers: newsletterRes.data.total_subscribers
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div>
      <h2 className="playfair text-3xl font-bold text-[#C9A961] mb-8">Dashboard Overview</h2>
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="text-4xl mb-2">🍾</div>
          <h3 className="text-gray-400 text-sm mb-1">Total Products</h3>
          <p className="text-3xl font-bold text-white">{stats.products}</p>
        </div>
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="text-4xl mb-2">🍳</div>
          <h3 className="text-gray-400 text-sm mb-1">Total Recipes</h3>
          <p className="text-3xl font-bold text-white">{stats.recipes}</p>
        </div>
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="text-4xl mb-2">💬</div>
          <h3 className="text-gray-400 text-sm mb-1">Unread Messages</h3>
          <p className="text-3xl font-bold text-white">{stats.messages}</p>
        </div>
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="text-4xl mb-2">📧</div>
          <h3 className="text-gray-400 text-sm mb-1">Newsletter Subscribers</h3>
          <p className="text-3xl font-bold text-white">{stats.subscribers}</p>
        </div>
      </div>
    </div>
  );
};

// Products Manager Component
const ProductsManager = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'extra_virgin',
    description: '',
    sizes: [],
    price: '',
    image_url: '',
    featured: false
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        sizes: formData.sizes.split(',').map(s => s.trim()).filter(Boolean),
        price: formData.price ? parseFloat(formData.price) : null
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully');
      }

      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      description: product.description,
      sizes: product.sizes.join(', '),
      price: product.price || '',
      image_url: product.image_url,
      featured: product.featured
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'extra_virgin',
      description: '',
      sizes: [],
      price: '',
      image_url: '',
      featured: false
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="playfair text-3xl font-bold text-[#C9A961]">Products Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-product-button"
          className="btn-gold-filled"
        >
          {showForm ? 'Cancel' : '+ Add Product'}
        </button>
      </div>

      {showForm && (
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  data-testid="product-category-select"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                >
                  <option value="extra_virgin">Extra Virgin</option>
                  <option value="premium">Premium</option>
                  <option value="organic">Organic</option>
                  <option value="gift_boxes">Gift Boxes</option>
                  <option value="accessories">Accessories</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
                data-testid="product-description-input"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Sizes (comma-separated)</label>
                <input
                  type="text"
                  value={formData.sizes}
                  onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                  placeholder="250ml, 500ml, 750ml"
                  data-testid="product-sizes-input"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Price (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  data-testid="product-price-input"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                />
              </div>
            </div>

            <ImageUploader
              category="products"
              currentImage={formData.image_url}
              onUploadSuccess={(url) => setFormData({ ...formData, image_url: url })}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                data-testid="product-featured-checkbox"
                className="w-4 h-4 text-[#C9A961] bg-[#1A1915] border-[#C9A961]/30"
              />
              <label htmlFor="featured" className="ml-2 text-sm text-gray-400">
                Featured Product
              </label>
            </div>

            <button type="submit" data-testid="product-submit-button" className="btn-gold-filled w-full">
              {editingProduct ? 'Update Product' : 'Create Product'}
            </button>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <div
            key={product.id}
            data-testid={`product-card-${product.id}`}
            className="bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20"
          >
            <img
              src={getImageUrl(product.image_url)}
              alt={product.name}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <span className="text-xs text-[#C9A961]">{product.category.replace('_', ' ')}</span>
              <h3 className="text-white font-semibold mt-1 mb-2">{product.name}</h3>
              <p className="text-gray-400 text-sm line-clamp-2 mb-3">{product.description}</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(product)}
                  data-testid={`edit-product-${product.id}`}
                  className="flex-1 px-3 py-2 border border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961] hover:text-black text-sm transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  data-testid={`delete-product-${product.id}`}
                  className="flex-1 px-3 py-2 border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white text-sm transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Recipes Manager Component
const RecipesManager = ({ token }) => {
  const [recipes, setRecipes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ingredients: '',
    instructions: '',
    image_url: '',
    category: 'main'
  });

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        ingredients: formData.ingredients.split('\n').filter(Boolean)
      };

      if (editingRecipe) {
        await axios.put(`${API}/recipes/${editingRecipe.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipe updated successfully');
      } else {
        await axios.post(`${API}/recipes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Recipe created successfully');
      }

      fetchRecipes();
      resetForm();
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error('Failed to save recipe');
    }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;

    try {
      await axios.delete(`${API}/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recipe deleted successfully');
      fetchRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Failed to delete recipe');
    }
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      title: recipe.title,
      description: recipe.description,
      ingredients: recipe.ingredients.join('\n'),
      instructions: recipe.instructions,
      image_url: recipe.image_url,
      category: recipe.category
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      ingredients: '',
      instructions: '',
      image_url: '',
      category: 'main'
    });
    setEditingRecipe(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="playfair text-3xl font-bold text-[#C9A961]">Recipes Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-recipe-button"
          className="btn-gold-filled"
        >
          {showForm ? 'Cancel' : '+ Add Recipe'}
        </button>
      </div>

      {showForm && (
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">
            {editingRecipe ? 'Edit Recipe' : 'Add New Recipe'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Recipe Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="recipe-title-input"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  data-testid="recipe-category-select"
                  className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
                >
                  <option value="appetizer">Appetizer</option>
                  <option value="main">Main Course</option>
                  <option value="dessert">Dessert</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="2"
                data-testid="recipe-description-input"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Ingredients (one per line) *</label>
              <textarea
                value={formData.ingredients}
                onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                required
                rows="6"
                data-testid="recipe-ingredients-input"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Instructions *</label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                required
                rows="6"
                data-testid="recipe-instructions-input"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              />
            </div>

            <ImageUploader
              category="recipes"
              currentImage={formData.image_url}
              onUploadSuccess={(url) => setFormData({ ...formData, image_url: url })}
            />

            <button type="submit" data-testid="recipe-submit-button" className="btn-gold-filled w-full">
              {editingRecipe ? 'Update Recipe' : 'Create Recipe'}
            </button>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recipes.map((recipe) => (
          <div
            key={recipe.id}
            data-testid={`recipe-card-${recipe.id}`}
            className="bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20"
          >
            <img
              src={getImageUrl(recipe.image_url)}
              alt={recipe.title}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <span className="text-xs text-[#C9A961]">{recipe.category}</span>
              <h3 className="text-white font-semibold mt-1 mb-2">{recipe.title}</h3>
              <p className="text-gray-400 text-sm line-clamp-2 mb-3">{recipe.description}</p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(recipe)}
                  data-testid={`edit-recipe-${recipe.id}`}
                  className="flex-1 px-3 py-2 border border-[#C9A961]/30 text-[#C9A961] hover:bg-[#C9A961] hover:text-black text-sm transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(recipe.id)}
                  data-testid={`delete-recipe-${recipe.id}`}
                  className="flex-1 px-3 py-2 border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white text-sm transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Gallery Manager Component
const GalleryManager = ({ token }) => {
  const [images, setImages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    category: 'products'
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gallery`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image added successfully');
      fetchImages();
      setFormData({ url: '', title: '', category: 'products' });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted successfully');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="playfair text-3xl font-bold text-[#C9A961]">Gallery Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-image-button"
          className="btn-gold-filled"
        >
          {showForm ? 'Cancel' : '+ Add Image'}
        </button>
      </div>

      {showForm && (
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Add New Image</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUploader
              category="gallery"
              currentImage={formData.url}
              onUploadSuccess={(url) => setFormData({ ...formData, url })}
            />
            <div>
              <label className="block text-sm text-gray-400 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="gallery-title-input"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                data-testid="gallery-category-select"
                className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white focus:border-[#C9A961] focus:outline-none"
              >
                <option value="terroir">Terroir</option>
                <option value="products">Products</option>
                <option value="recipes">Recipes</option>
              </select>
            </div>
            <button type="submit" data-testid="gallery-submit-button" className="btn-gold-filled w-full">
              Add Image
            </button>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
        {images.map((image) => (
          <div
            key={image.id}
            data-testid={`gallery-image-${image.id}`}
            className="bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20"
          >
            <img loading="lazy" decoding="async" src={getImageUrl(image.url)} alt={image.title} className="w-full h-48 object-cover" />
            <div className="p-4">
              <span className="text-xs text-[#C9A961]">{image.category}</span>
              <h3 className="text-white font-semibold mt-1 mb-3 text-sm">{image.title}</h3>
              <button
                onClick={() => handleDelete(image.id)}
                data-testid={`delete-image-${image.id}`}
                className="w-full px-3 py-2 border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white text-sm transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Messages Manager Component
const MessagesManager = ({ token }) => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message marked as read');
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Failed to update message');
    }
  };

  return (
    <div>
      <h2 className="playfair text-3xl font-bold text-[#C9A961] mb-8">Contact Messages</h2>

      <div className="space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            data-testid={`message-${message.id}`}
            className={`bg-[#2A2720] p-6 rounded-lg border ${
              message.read ? 'border-[#C9A961]/10' : 'border-[#C9A961]/30'
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-white font-semibold text-lg">{message.name}</h3>
                <p className="text-gray-400 text-sm">{message.email}</p>
                {message.company && (
                  <p className="text-gray-500 text-sm">{message.company}</p>
                )}
              </div>
              <div className="text-right">
                <span className={`inline-block px-3 py-1 rounded text-xs ${
                  message.read ? 'bg-gray-700 text-gray-400' : 'bg-[#C9A961] text-black'
                }`}>
                  {message.read ? 'Read' : 'Unread'}
                </span>
                <p className="text-gray-500 text-xs mt-1">
                  {new Date(message.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <p className="text-gray-300 mb-4">{message.message}</p>

            {!message.read && (
              <button
                onClick={() => handleMarkRead(message.id)}
                data-testid={`mark-read-${message.id}`}
                className="btn-gold text-sm px-4 py-2"
              >
                Mark as Read
              </button>
            )}
          </div>
        ))}

        {messages.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            No messages yet
          </div>
        )}
      </div>
    </div>
  );
};

// Newsletter Manager Component
const NewsletterManager = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSubscribers();
    fetchStats();
  }, [token]);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Erreur lors du chargement des abonnés');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/newsletter/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats({ total: response.data.total_subscribers });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Désinscrire ${email} de la newsletter ?`)) return;
    
    try {
      await axios.delete(`${API}/newsletter/unsubscribe/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Abonné désinscrit');
      fetchSubscribers();
      fetchStats();
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Erreur lors de la désinscription');
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Date d\'inscription', 'Statut'];
    const csvContent = [
      headers.join(','),
      ...subscribers.map(sub => [
        sub.email,
        new Date(sub.created_at).toLocaleDateString('fr-FR'),
        sub.active ? 'Actif' : 'Inactif'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export CSV téléchargé');
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A961]">Chargement...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white playfair">Newsletter</h1>
          <p className="text-gray-400 mt-1">Gérez vos abonnés à la newsletter</p>
        </div>
        <button
          onClick={exportToCSV}
          data-testid="export-csv-btn"
          className="btn-gold px-6 py-3 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>Exporter CSV</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Abonnés</p>
              <p className="text-3xl font-bold text-[#C9A961]" data-testid="total-subscribers">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-[#C9A961]/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Ce mois</p>
              <p className="text-3xl font-bold text-white">
                {subscribers.filter(s => {
                  const subDate = new Date(s.created_at);
                  const now = new Date();
                  return subDate.getMonth() === now.getMonth() && subDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-[#2A2720] p-6 rounded-lg border border-[#C9A961]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Actifs</p>
              <p className="text-3xl font-bold text-white">
                {subscribers.filter(s => s.active).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Rechercher par email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-subscribers"
          className="w-full md:w-96 px-4 py-3 bg-[#2A2720] border border-[#C9A961]/30 text-white placeholder-gray-500 focus:border-[#C9A961] focus:outline-none rounded"
        />
      </div>

      {/* Subscribers Table */}
      <div className="bg-[#2A2720] rounded-lg border border-[#C9A961]/20 overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#1A1915] border-b border-[#C9A961]/20">
            <tr>
              <th className="text-left px-6 py-4 text-[#C9A961] font-semibold">Email</th>
              <th className="text-left px-6 py-4 text-[#C9A961] font-semibold">Date d'inscription</th>
              <th className="text-left px-6 py-4 text-[#C9A961] font-semibold">Statut</th>
              <th className="text-right px-6 py-4 text-[#C9A961] font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscribers.map((subscriber, index) => (
              <tr
                key={subscriber.id}
                data-testid={`subscriber-row-${index}`}
                className="border-b border-[#C9A961]/10 hover:bg-[#1A1915]/50 transition-colors"
              >
                <td className="px-6 py-4">
                  <span className="text-white">{subscriber.email}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-gray-400">
                    {new Date(subscriber.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-block px-3 py-1 rounded text-xs ${
                    subscriber.active 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {subscriber.active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => handleUnsubscribe(subscriber.email)}
                    data-testid={`unsubscribe-${index}`}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    Désinscrire
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredSubscribers.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {searchTerm ? 'Aucun résultat trouvé' : 'Aucun abonné pour le moment'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
